<script setup lang="ts">
import { defineProps, ref, computed, useAttrs } from "vue";
import { usePortfolioStore } from "@/stores/portfolio";
import { useSkillsState } from "@/composables/useSkillsState";
import type { IndustryType } from "@/types";
import { additionalSkills, anthropicCv } from "@/data/portfolio";
import { getCvVariant } from "@/utils/cvVariant";
import ExperienceList from "./ExperienceList.vue";
import Skills from "../Skills.vue";
import PrintableSkills from "../PrintableSkills.vue";
import MainSkills from "./MainSkills.vue";
import { ALL_MAIN_SKILLS } from "./skillCategories";
import { ChevronDownIcon } from "@heroicons/vue/24/solid";

// Props definition
export interface Props {
  sectionClass?: string;
}

const { sectionClass } = defineProps<Props>();
const attrs = useAttrs();
const isAnthropicCv = getCvVariant() === 'anthropic';
const featuredExperienceIds = new Set<string>(anthropicCv.featuredExperienceIds);

// Use the new store and composables
const portfolioStore = usePortfolioStore();
const { selectedSkills, filteredExperiences } = useSkillsState();

// State for selected industries for filtering (if filter is enabled)
const selectedIndustries = ref<IndustryType[]>([]);

// Computed for filtering experiences based on selected industries
const filteredWorkExperiences = computed(() => {
  const experiences = isAnthropicCv
    ? filteredExperiences.value.filter((experience) => featuredExperienceIds.has(experience.id))
    : filteredExperiences.value;

  if (selectedIndustries.value.length > 0) {
    return experiences.filter((we) =>
        selectedIndustries.value.includes(we.industry as IndustryType)
    );
  }
  return experiences;
});

// Computed for "Other Skills"
const otherSkills = computed(() => {
  const allSkills = portfolioStore.allSkills;

  const workExperienceSkills = allSkills
      .map((s) => s.label)
      .filter((sl) => !ALL_MAIN_SKILLS.includes(sl));
  
  // Combine with additional comprehensive skills
  const allOtherSkills = [...new Set([...workExperienceSkills, ...additionalSkills])];
  
  return allOtherSkills;
});

/**
 * Function to clear all filters: selected industries and selected skills.
 */
const clearFilters = () => {
  selectedIndustries.value = []; // Clear selected industries
  portfolioStore.clearSkills(); // Clear selected skills
};

/**
 * Computed property to determine if any skill is selected.
 * Returns true if at least one skill is selected.
 */
const hasSelectedSkills = computed(() => {
  return selectedSkills.value.length > 0;
});
</script>

<template>
  <div v-bind="attrs">
    <!-- Main Skills Section -->
    <section :class="[sectionClass, { 'cv-target-skills': isAnthropicCv }]" aria-labelledby="core-expertise-heading">
      <p class="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300">Capabilities</p>
      <h2 id="core-expertise-heading" class="mb-2 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">Core expertise</h2>
      <p class="mb-6 max-w-3xl text-slate-600 dark:text-slate-300">
        Select any skill to filter the experience timeline and see where I have applied it.
      </p>
      <MainSkills
          v-if="!isAnthropicCv"
          class="print:grid print:grid-cols-3"
          section-class="text-left"
      />
      <div v-else class="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-4">
        <div v-for="section in anthropicCv.skillSections" :key="section.title" class="rounded-xl border border-slate-200 bg-slate-50 p-5 text-left dark:border-slate-700 dark:bg-slate-800/60">
          <h3 class="font-bold text-slate-950 dark:text-white">{{ section.title }}</h3>
          <Skills :skills="[...section.skills]" class="screen-skill mt-4" />
          <PrintableSkills :skills="[...section.skills]" class="hidden print:block text-xs text-left" />
        </div>
      </div>
    </section>

    <!-- Other Skills Section -->
    <details v-if="!isAnthropicCv" :class="sectionClass" class="group mb-14 rounded-xl border border-slate-200 dark:border-slate-700 print:hidden">
      <summary class="cursor-pointer list-none px-5 py-4 font-semibold text-slate-700 hover:text-cyan-700 dark:text-slate-200 dark:hover:text-cyan-300">
        <span class="inline-flex items-center gap-2">
          <ChevronDownIcon class="h-5 w-5 text-cyan-700 transition-transform group-open:rotate-180 dark:text-cyan-300" aria-hidden="true" />
          Browse {{ otherSkills.length }} additional technologies
        </span>
      </summary>
      <Skills
          :skills="otherSkills"
          sort="weight"
          class="screen-skill border-t border-slate-200 px-4 py-4 dark:border-slate-700"
      />
    </details>

    <!-- Work Experience Section -->
    <section id="work-experience" :class="sectionClass" aria-labelledby="work-experience-heading">
      <p class="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300">Track record</p>
      <div class="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h2 id="work-experience-heading" class="mb-2 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">Work experience</h2>
          <p class="mb-0 max-w-2xl text-slate-600 dark:text-slate-300">From systems administration and software engineering to platform architecture and DevSecOps leadership.</p>
        </div>

      <!-- Remove All Filters Button -->
      <button
          v-if="hasSelectedSkills"
          @click="clearFilters"
          class="shrink-0 rounded-lg border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950"
      >
        Clear skill filters
      </button>
      </div>

      <!-- Experience List -->
      <ExperienceList
          v-if="filteredWorkExperiences.length > 0"
          class="grid grid-cols-1"
          :experiences="filteredWorkExperiences"
      />
      <p
          v-else-if="hasSelectedSkills"
          class="text-center text-gray-500 dark:text-gray-400 py-8"
      >
        No experiences match the selected skill filters.
      </p>

      <div v-if="isAnthropicCv" class="mt-4 text-left print:text-xs">
        <h2 class="mb-2 print:break-after-avoid">Additional Experience</h2>
        <p>
          Earlier career (1999–2021): software, systems, cloud, DevOps, and technical-lead positions across
          Bitheater consulting engagements, Telefónica, Universitat Jaume I, and EADS CASA Espacio. Work
          included CI/CD and cloud migration, developer tooling, distributed web platforms, embedded systems,
          robotics, and enterprise UNIX/Linux infrastructure.
        </p>
      </div>
    </section>
  </div>
</template>
